/**
 * PM-D38 (projects/v1@2.10.0) — per-project AI-model selection: ONE multi-select.
 *
 * Supersedes the PM-AI-2 two-select widget (defect/finding + segmentation). The
 * 3 categories (object-detection / segmentation / classification) are the
 * model's taxonomy `modelType`, NOT a user selection bucket — and a segmenter
 * emits BOTH findings and component-segmentation, so splitting the *selection*
 * by output-type was wrong (PM-D38). The user simply picks WHICH models to run.
 *
 * Options come from `useAiModelsQuery()` (taxonomy/v1 `GET /ai-finding-models`).
 * Offered models are filtered to:
 *   - ACTIVE (`isActive`), and
 *   - NOT the "None" model — the synthetic empty model identified by
 *     `classCount === 0` (the rule, never a hard-coded UUID — see
 *     [[reference_none_class_model_uuid]]).
 *
 * The value is a flat `string[]` of taxonomy modelIds; `onChange` returns that
 * flat id list. Each option shows its `modelType` as a small secondary hint, but
 * it is ONE list and ONE selector.
 *
 * Cost hint (PM-D40): a subtle, non-alarming note that each selected model runs
 * on every dataset, so more models = more processing time and cost. Rendered
 * here so it appears in BOTH the create wizard and the project-detail edit panel
 * that reuse this widget.
 *
 * Selection is OPTIONAL — a project may pick no AI models at all.
 */
import { useMemo, type ReactElement } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import type { AiFindingModel, AiFindingModelType } from "@aeriosiq/types";
import { useAiModelsQuery } from "../ai-models/aiModelQueries.js";

/** Friendly label for the per-option model-type hint (one list — the type is a
 *  hint only, not a grouping). */
const MODEL_TYPE_LABEL: Record<AiFindingModelType, string> = {
  object_detection: "Object detection",
  classification: "Classification",
  segmentation: "Segmentation",
};

/** PM-D40 — cost-awareness note shown next to the selector in both surfaces. */
export const AI_MODEL_COST_HINT =
  "Each selected model runs on every dataset in this project — more models means more processing time and cost.";

/** PM-D39 — helper shown once the tenant cap is reached. */
export function maxModelsHelper(max: number): string {
  return `Maximum ${String(max)} AI model${max === 1 ? "" : "s"} for this tenant.`;
}

export interface AiModelMultiSelectProps {
  /** Selected AI-model ids (taxonomy modelIds). */
  value: readonly string[];
  /** Returns the flat id list on every change. */
  onChange: (next: string[]) => void;
  /**
   * PM-D39 — optional per-tenant cap on how many models may be selected.
   * `null`/`undefined` = no cap. When set, once `value.length >= maxSelections`
   * the unselected options are disabled (can't add a new one) and a helper
   * shows the limit. The BE is the source of truth (it 422s over-limit); this
   * is the UX guard so the user can't exceed it.
   *
   * Edit-already-over-limit (a project carries MORE than a newly-lowered
   * limit): we DON'T destructively trim. Selecting MORE is blocked, but the
   * existing over-limit set stays selected and removable — so the user can
   * save the current set or pare it down, never silently lose a selection.
   */
  maxSelections?: number | null;
}

/**
 * The AI section of the Create Project wizard (Step 1) and the project-detail
 * AI-models edit panel. ONE multi-select fed from all active taxonomy AI models
 * (minus the "None" model). Optional.
 */
export function AiModelMultiSelect({
  value,
  onChange,
  maxSelections,
}: AiModelMultiSelectProps): ReactElement {
  const modelsQuery = useAiModelsQuery();
  const loading = modelsQuery.isLoading;

  // PM-D39 — a positive numeric cap means "block adding past it". null /
  // undefined / non-positive = no cap (cap = null).
  const cap =
    typeof maxSelections === "number" && maxSelections > 0
      ? maxSelections
      : null;
  // At/over the cap once the current selection meets or exceeds it (>= covers
  // the edit-already-over-limit case — we never trim, just block adding more).
  const atCap = cap !== null && value.length >= cap;

  // Helper line under the selector — the cost hint always, prefixed with the
  // "Maximum N" note once the cap is reached.
  const helperText =
    cap !== null && atCap
      ? `${maxModelsHelper(cap)} ${AI_MODEL_COST_HINT}`
      : AI_MODEL_COST_HINT;

  // Active models only, excluding the synthetic "None" model (classCount === 0).
  const options = useMemo(
    () =>
      (modelsQuery.data?.items ?? [])
        .filter((m) => m.isActive && m.classCount > 0)
        .sort((a, b) => a.modelName.localeCompare(b.modelName)),
    [modelsQuery.data?.items],
  );

  // Resolve the selected ids back to model objects (the Autocomplete value).
  const selectedModels = useMemo(
    () => options.filter((m) => value.includes(m.modelId)),
    [options, value],
  );

  const noModelsAtAll = !loading && options.length === 0;

  return (
    <Box component="section" aria-label="AI models">
      <Typography variant="subtitle2" gutterBottom>
        AI models
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Choose which AI models this project runs on its datasets. Optional — you
        can leave it empty.
      </Typography>

      {noModelsAtAll ? (
        <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
          No AI models are configured for your organisation yet. You can save
          the project without selecting any, and add models later.
        </Alert>
      ) : (
        <Box sx={{ mt: 1.5 }}>
          <Autocomplete<AiFindingModel, true>
            multiple
            disableCloseOnSelect
            options={options}
            value={selectedModels}
            loading={loading}
            getOptionLabel={(m) => m.modelName}
            isOptionEqualToValue={(a, b) => a.modelId === b.modelId}
            // PM-D39 — at the cap, disable any option not already selected so a
            // new one can't be added (removing a selected one stays allowed).
            getOptionDisabled={(m) => atCap && !value.includes(m.modelId)}
            onChange={(_e, next) => {
              const nextIds = next.map((m) => m.modelId);
              // Belt-and-braces: never let the selection GROW past the cap
              // (e.g. via keyboard). Shrinking / reordering is always allowed.
              if (atCap && nextIds.length > value.length) {
                return;
              }
              onChange(nextIds);
            }}
            noOptionsText="No active AI models available."
            renderOption={(props, m) => (
              <li {...props} key={m.modelId}>
                <Box component="span">{m.modelName}</Box>
                <Box component="span" sx={{ ml: 1, color: "text.secondary" }}>
                  — {MODEL_TYPE_LABEL[m.modelType]}
                </Box>
              </li>
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((m, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    size="small"
                    label={m.modelName}
                    {...tagProps}
                  />
                );
              })
            }
            renderInput={(params) => {
              /* eslint-disable @typescript-eslint/no-explicit-any */
              const passThrough: any = params;
              return (
                <TextField
                  {...passThrough}
                  size="small"
                  label="AI models"
                  helperText={helperText}
                  slotProps={{
                    htmlInput: {
                      ...params.inputProps,
                      "aria-label": "AI models",
                    },
                  }}
                />
              );
              /* eslint-enable */
            }}
          />
        </Box>
      )}
    </Box>
  );
}
